import React from 'react';

// ============================================================================
// Module-level helper functions for cognitive complexity reduction
// ============================================================================

/**
 * URL display text mappings - maps hostname patterns to friendly display names
 */
const URL_DISPLAY_MAPPINGS = [
  { pattern: 'appt.link', text: 'Schedule a meeting', checkPath: false },
  { pattern: 'meet', text: 'Schedule a meeting', checkPath: true },
  { pattern: 'linkedin', text: 'LinkedIn', checkPath: false },
  { pattern: 'facebook', text: 'Facebook', checkPath: false },
  { pattern: 'twitter', text: 'Twitter/X', checkPath: false },
  { pattern: 'x.com', text: 'Twitter/X', checkPath: false },
  { pattern: 'instagram', text: 'Instagram', checkPath: false },
  { pattern: 'youtube', text: 'YouTube', checkPath: false },
];

/**
 * Gets friendly display text for a URL
 * @param {string} url - The URL to convert
 * @param {string} fallback - Fallback text if URL can't be parsed
 * @returns {string} - Friendly display text
 */
const getUrlDisplayText = (url, fallback = 'Click here') => {
  try {
    const urlObj = new URL(url);

    for (const mapping of URL_DISPLAY_MAPPINGS) {
      if (mapping.checkPath) {
        if (urlObj.pathname.includes(mapping.pattern)) {
          return mapping.text;
        }
      } else {
        if (urlObj.hostname.includes(mapping.pattern)) {
          return mapping.text;
        }
      }
    }

    // Default: show cleaned hostname
    return urlObj.hostname.replace('www.', '');
  } catch (e) {
    return fallback;
  }
};

/**
 * Header level configuration - maps markdown header levels to React elements
 */
const HEADER_CONFIG = {
  1: { tag: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-2', keyPrefix: 'h1' },
  2: { tag: 'h4', className: 'text-base font-semibold text-gray-800 mt-3 mb-2', keyPrefix: 'h2' },
  3: { tag: 'h5', className: 'text-sm font-semibold text-gray-700 mt-2 mb-1', keyPrefix: 'h3' },
  4: { tag: 'h6', className: 'text-sm font-medium text-gray-600 mt-2 mb-1', keyPrefix: 'h4' },
};

/**
 * Creates a header element based on markdown header level
 * @param {number} level - Header level (1-4)
 * @param {string} text - Header text content
 * @param {number} index - Line index for key generation
 * @param {function} formatFn - Function to format inline text
 * @returns {React.Element} - Header element
 */
const createHeaderElement = (level, text, index, formatFn) => {
  const config = HEADER_CONFIG[level] || HEADER_CONFIG[4];
  const Tag = config.tag;
  return (
    <Tag key={`${config.keyPrefix}-${index}`} className={config.className}>
      {formatFn(text)}
    </Tag>
  );
};

/**
 * Formats a phone number for display
 * @param {string} rawPhone - Raw phone number with special chars removed
 * @param {string} digits - Digits only from the phone number
 * @param {string} original - Original matched phone string
 * @returns {string} - Formatted phone number for display
 */
const formatPhoneDisplay = (rawPhone, digits, original) => {
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return original;
};

/**
 * Link styles shared across all link types
 */
const LINK_CLASS_NAME = 'text-[var(--g6-primary)] hover:text-[#008A44] underline font-medium';

/**
 * Creates a link element with consistent styling
 * @param {string} href - Link URL or mailto/tel
 * @param {string} text - Display text
 * @param {string} keyPrefix - Prefix for React key
 * @param {number} index - Index for key generation
 * @param {boolean} external - Whether link opens in new tab
 * @returns {React.Element} - Link element
 */
const createLinkElement = (href, text, keyPrefix, index, external = true) => {
  const props = {
    key: `${keyPrefix}-${index}`,
    href,
    className: LINK_CLASS_NAME,
  };

  if (external) {
    props.target = '_blank';
    props.rel = 'noopener noreferrer';
  }

  return <a {...props}>{text}</a>;
};

/**
 * MessageFormatter Component
 * Formats chatbot messages with support for:
 * - **Bold text**
 * - *Italic text*
 * - Bullet points (lines starting with -, *, or •)
 * - Numbered lists (lines starting with 1., 2., etc.)
 * - Line breaks
 * - Links
 */
const MessageFormatter = ({ content }) => {
  const formatText = (text) => {
    // Split by line breaks
    const lines = text.split('\n');
    const formatted = [];
    let listItems = [];
    let listType = null;
    let listStartIndex = 0;

    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'numbered') {
          formatted.push(
            <ol key={`ol-${listStartIndex}`} className="list-decimal list-inside mb-3 space-y-1 ml-2">
              {listItems.map((item, idx) => React.cloneElement(item, { key: `num-${listStartIndex}-${idx}` }))}
            </ol>
          );
        } else if (listType === 'bullet') {
          formatted.push(
            <ul key={`ul-${listStartIndex}`} className="list-disc list-inside mb-3 space-y-1 ml-2">
              {listItems.map((item, idx) => React.cloneElement(item, { key: `bullet-${listStartIndex}-${idx}` }))}
            </ul>
          );
        }
        listItems = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Check for headers (#### Header, ### Header, ## Header, # Header)
      // Bounded to prevent ReDoS - max 500 chars for header text
      const headerMatch = trimmedLine.match(/^(#{1,4})\s+(.{1,500})$/);
      if (headerMatch) {
        flushList();
        const level = headerMatch[1].length;
        formatted.push(createHeaderElement(level, headerMatch[2], index, formatInlineText));
        return;
      }

      // Check for numbered list (1., 2., etc.)
      // Bounded to prevent ReDoS - max 2000 chars per line
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.{1,2000})$/);
      if (numberedMatch) {
        if (listType !== 'numbered') {
          flushList();
          listType = 'numbered';
          listStartIndex = index;
        }
        listItems.push(
          <li className="mb-1">
            {formatInlineText(numberedMatch[2])}
          </li>
        );
        return;
      }

      // Check for bullet points (-, *, •)
      // Bounded to prevent ReDoS - max 2000 chars per line
      const bulletMatch = trimmedLine.match(/^[-*•]\s+(.{1,2000})$/);
      if (bulletMatch) {
        if (listType !== 'bullet') {
          flushList();
          listType = 'bullet';
          listStartIndex = index;
        }
        listItems.push(
          <li className="mb-1">
            {formatInlineText(bulletMatch[1])}
          </li>
        );
        return;
      }

      // If we were building a list and this line isn't part of it, flush the list
      if (listType && !bulletMatch && !numberedMatch) {
        flushList();
      }

      // Regular paragraph
      if (trimmedLine) {
        formatted.push(
          <p key={`p-${index}`} className="mb-2">
            {formatInlineText(line)}
          </p>
        );
      } else if (formatted.length > 0) {
        // Empty line - add spacing
        formatted.push(<div key={`space-${index}`} className="h-2" />);
      }
    });

    // Don't forget to flush the last list if there is one
    flushList();

    return formatted;
  };

  const formatInlineText = (text) => {
    const parts = [];
    let currentIndex = 0;

    // Regex to match formatting patterns (bounded quantifiers to prevent ReDoS)
    const regex = /(\*\*(.{1,1000}?)\*\*)|(\*(.{1,1000}?)\*)|(\[([^\]]{1,500})\]\(([^)]{1,2000})\))|(\[(https?:\/\/[^\]]{1,2000})\])|((https?:\/\/[^\s<>\[\]]{1,2000}))|([a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,255}\.[a-zA-Z]{2,10})|(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }

      if (match[1]) {
        // Bold text **text**
        parts.push(
          <strong key={`bold-${match.index}`} className="font-bold text-gray-900">
            {formatInlineText(match[2])}
          </strong>
        );
      } else if (match[3]) {
        // Italic text *text*
        parts.push(
          <em key={`italic-${match.index}`} className="italic">
            {formatInlineText(match[4])}
          </em>
        );
      } else if (match[5]) {
        // Link [text](url)
        parts.push(createLinkElement(match[7], match[6], 'link', match.index));
      } else if (match[8]) {
        // URL wrapped in square brackets [url]
        const url = match[9];
        parts.push(createLinkElement(url, getUrlDisplayText(url, 'Click here'), 'bracketlink', match.index));
      } else if (match[10]) {
        // Plain URL (https://... or http://...)
        const url = match[11];
        parts.push(createLinkElement(url, getUrlDisplayText(url, url), 'plainurl', match.index));
      } else if (match[12]) {
        // Email address
        parts.push(createLinkElement(`mailto:${match[12]}`, match[12], 'email', match.index, false));
      } else if (match[13]) {
        // Phone number
        const rawPhone = match[13].replace(/[-.\s()]/g, '');
        const digits = rawPhone.replace(/\D/g, '');
        const displayPhone = formatPhoneDisplay(rawPhone, digits, match[13]);
        parts.push(createLinkElement(`tel:${rawPhone}`, displayPhone, 'phone', match.index, false));
      }

      currentIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return <div className="formatted-message">{formatText(content)}</div>;
};

export default MessageFormatter;
